import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import applaiLogo from '../assets/applai-logo.png';
import ProfileModal from './ProfileModal';
import ContactModal from './ContactModal';
import SettingsModal from './SettingsModal';
import UpgradeModal from './UpgradeModal';
import BillingModal from './BillingModal';
import './Navbar.css';

const Navbar = ({ onAddJob, onAddEvent, activePage, setActivePage }) => {
  const { user, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showBilling, setShowBilling] = useState(false);
  const menuRef = useRef(null);
  const addBtnRef = useRef(null);
  const navRef = useRef(null);
  const [sliderStyle, setSliderStyle] = useState({ left: 0, width: 0, opacity: 0 });

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
      if (addBtnRef.current && !addBtnRef.current.contains(e.target)) setAddMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useLayoutEffect(() => {
    const updateSlider = () => {
      if (!navRef.current) return;
      const activeEl = navRef.current.querySelector('.navbar-nav-item.active');
      if (!activeEl) return;
      const navRect = navRef.current.getBoundingClientRect();
      const itemRect = activeEl.getBoundingClientRect();
      setSliderStyle({ left: itemRect.left - navRect.left, width: itemRect.width, opacity: 1 });
    };

    updateSlider();

    // Snap slider instantly on resize (no animation lag when dragging window edge)
    let raf;
    let timer;
    const onResize = () => {
      const sliderEl = navRef.current?.querySelector('.navbar-nav-slider');
      if (sliderEl) sliderEl.style.transition = 'none';
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(updateSlider);
      clearTimeout(timer);
      timer = setTimeout(() => {
        const s = navRef.current?.querySelector('.navbar-nav-slider');
        if (s) s.style.transition = '';
      }, 200);
    };

    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [activePage]);

  const close = (fn) => () => { setMenuOpen(false); fn(); };

  return (
    <>
    <nav className="navbar">
      <div className="navbar-inner">
        <div className="navbar-brand" onClick={() => setActivePage('tracker')}>
          <img src={applaiLogo} alt="ApplAI" className="navbar-logo-img" />
          <span className="navbar-logo-text">Appl<span className="navbar-logo-ai">AI</span></span>
        </div>

        <div className="navbar-nav" ref={navRef}>
          <div
            className="navbar-nav-slider"
            style={{ left: sliderStyle.left, width: sliderStyle.width, opacity: sliderStyle.opacity }}
          />
          <button className={`navbar-nav-item ${activePage === 'tracker' ? 'active' : ''}`} onClick={() => setActivePage('tracker')}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
              <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
            </svg>
            <span>Applications</span>
          </button>
          <button className={`navbar-nav-item ${activePage === 'calendar' ? 'active' : ''}`} onClick={() => setActivePage('calendar')}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/>
              <line x1="8" y1="2" x2="8" y2="6"/><line x1="16" y1="2" x2="16" y2="6"/>
            </svg>
            <span>Schedule</span>
          </button>
          <button className={`navbar-nav-item ${activePage === 'analyze' ? 'active' : ''}`} onClick={() => setActivePage('analyze')}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <span>Analyze</span>
          </button>
          <button className={`navbar-nav-item ${activePage === 'prepare' ? 'active' : ''}`} onClick={() => setActivePage('prepare')}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/>
            </svg>
            <span>Prepare</span>
          </button>
        </div>

        <div className="navbar-right">
          {/* Credits indicator */}
          <button
            className={`navbar-credits-pill ${user?.tier === 'pro' ? 'navbar-credits-pro' : user?.creditsRemaining <= 3 ? 'navbar-credits-low' : ''}`}
            onClick={() => setShowUpgrade(true)}
            title={user?.tier === 'pro' ? `Pro · ${user.creditsRemaining} of ${user.creditsLimit} credits left` : `Starter · ${user?.creditsRemaining ?? 15} of 15 credits left · Click to upgrade`}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
            </svg>
            <span>{user?.creditsRemaining ?? 15}</span>
            {user?.tier === 'pro' && <span className="navbar-credits-tier">PRO</span>}
          </button>

          <div className="navbar-add-wrap" ref={addBtnRef}>
            <button className={`navbar-add-btn${addMenuOpen ? ' active' : ''}`} onClick={() => setAddMenuOpen(v => !v)} title="Add Job or Event">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
            </button>
            {addMenuOpen && (
              <div className="navbar-add-menu">
                <button className="navbar-add-menu-item" onClick={() => { setAddMenuOpen(false); onAddJob(); }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/>
                  </svg>
                  Add Job
                </button>
                <button className="navbar-add-menu-item" onClick={() => { setAddMenuOpen(false); onAddEvent?.(); }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/>
                    <line x1="8" y1="2" x2="8" y2="6"/><line x1="16" y1="2" x2="16" y2="6"/>
                  </svg>
                  Add Event
                </button>
              </div>
            )}
          </div>

          <div className="navbar-user" ref={menuRef}>
            <button className="navbar-avatar-btn" onClick={() => setMenuOpen(!menuOpen)}>
              {user?.avatar
                ? <img src={user.avatar} alt={user.name} className="navbar-avatar-img" />
                : <span className="navbar-avatar-initials">{user?.name?.[0]?.toUpperCase()}</span>
              }
            </button>

            {menuOpen && (
              <div className="navbar-menu">
                <div className="navbar-menu-user">
                  <div className="navbar-menu-user-top">
                    <span className="navbar-menu-name">{user?.name}</span>
                    <span className={`navbar-menu-tier ${user?.tier === 'pro' ? 'navbar-menu-tier-pro' : ''}`}>{user?.tier === 'pro' ? 'Pro' : 'Starter'}</span>
                  </div>
                  <span className="navbar-menu-email">{user?.email}</span>
                  <div className="navbar-menu-credits">
                    <div className="navbar-menu-credits-bar-wrap">
                      <div className="navbar-menu-credits-bar" style={{ width: `${Math.max(0, Math.min(100, ((user?.creditsRemaining ?? 15) / (user?.creditsLimit ?? 15)) * 100))}%`, background: user?.creditsRemaining <= 3 ? '#EF4444' : user?.tier === 'pro' ? '#2563EB' : '#10B981' }} />
                    </div>
                    <span className="navbar-menu-credits-label">{user?.creditsRemaining ?? 15} / {user?.creditsLimit ?? 15} credits remaining</span>
                  </div>
                </div>
                <div className="navbar-menu-divider" />
                {user?.tier !== 'pro' && (
                  <button className="navbar-menu-item navbar-menu-upgrade" onClick={close(() => setShowUpgrade(true))}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                    </svg>
                    Upgrade to Pro
                  </button>
                )}
                <div className="navbar-menu-divider" />
                <button className="navbar-menu-item" onClick={close(() => setShowProfile(true))}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                  My Profile & Qualifications
                </button>
                <button className="navbar-menu-item" onClick={close(() => setShowBilling(true))}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <rect x="1" y="4" width="22" height="16" rx="2"/>
                    <line x1="1" y1="10" x2="23" y2="10"/>
                  </svg>
                  Billing
                </button>
                <button className="navbar-menu-item" onClick={close(() => setShowContact(true))}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  Contact / Report an Issue
                </button>
                <button className="navbar-menu-item" onClick={close(() => setShowSettings(true))}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M19.07 4.93a10 10 0 010 14.14M4.93 4.93a10 10 0 000 14.14"/>
                    <path d="M12 2v2M12 20v2M2 12h2M20 12h2"/>
                  </svg>
                  Settings
                </button>
                <div className="navbar-menu-divider" />
                <button className="navbar-menu-item logout" onClick={logout}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                    <polyline points="16 17 21 12 16 7"/>
                    <line x1="21" y1="12" x2="9" y2="12"/>
                  </svg>
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
    {showProfile  && <ProfileModal  onClose={() => setShowProfile(false)} onUpgrade={() => { setShowProfile(false); setShowUpgrade(true); }} />}
    {showContact  && <ContactModal  onClose={() => setShowContact(false)} />}
    {showSettings && <SettingsModal onClose={() => setShowSettings(false)} />}
    {showUpgrade  && <UpgradeModal  onClose={() => setShowUpgrade(false)} />}
    {showBilling  && <BillingModal  onClose={() => setShowBilling(false)} onUpgrade={() => { setShowBilling(false); setShowUpgrade(true); }} />}
    </>
  );
};

export default Navbar;
